import { mat4, quat, vec3, vec4 } from 'gl-matrix';
import {
  DepthMode,
  DrawableObject,
  RenderConfiguration,
  RenderMode,
  RenderSettings,
} from '../renderer';
import { BoundingDimensions, TRS } from '../util';
import { PropertyAnimation } from '../animation';
import { ObjectInteraction } from './ObjectManager';
import { Mesh } from '../mesh';
import { Animation, GltfAnimation } from '../animation';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import {
  GBufferShader,
  GBufferShaderSettings,
  GBufferTerrainShader,
  GBufferTerrainShaderSettings,
  DepthShader,
  PickingShader,
  ForwardTransparentShader,
  ForwardTransparentShaderSettings,
} from '../shader/generated';
import { AnimationState } from '../animation/AnimationState';
import { IkSystem } from '../animation/ik';
import { Material, TerrainMaterial } from '../material';
import { Texture } from '../texture';
import { Attachment, AttachmentHolder } from './attachments/Attachment';
import { bindTransparentLighting, findCsmLight } from './shaderBindingHelpers';

/** Structural shape covering the material setters shared by GBuffer & ForwardTransparent shaders. */
interface MaterialUniforms {
  setTextureSampler(texture: Texture): MaterialUniforms;
  setNormalMapSampler(texture: Texture): MaterialUniforms;
  setMetallicRoughnessSampler(texture: Texture): MaterialUniforms;
  setPresetColor(value: number[]): MaterialUniforms;
}

type AnyShader = GBufferShader | DepthShader | PickingShader | ForwardTransparentShader;

export class RenderObject implements DrawableObject {
  private _material?: Material | TerrainMaterial;

  public bounds!: BoundingDimensions;
  public renderConfiguration: RenderConfiguration = {
    static: false,
    order: 1000,
    modes: [
      RenderMode.Primary,
      RenderMode.GBuffer,
      RenderMode.Picking,
      RenderMode.Occlusion,
      RenderMode.Depth,
    ],
  };
  public animatedProperties?: PropertyAnimation;

  public interaction?: ObjectInteraction;

  public mesh: Mesh;

  public trs: TRS;

  public get position(): vec3 | number[] {
    return this.trs.position;
  }

  /**
   * The asset's "rest" orientation captured at load-time (model+mesh+object
   * transform composition). Movement code (`rotateTo`) composes a yaw on top:
   * `final = yawQuat * fixedRotationQuat`. Stored as a quaternion so the
   * full base orientation (X/Y/Z) survives — the legacy `fixedRotation:
   * number` only tracked Y and silently dropped any X/Z tilt during yaw
   * updates.
   */
  public fixedRotationQuat: quat;

  public get material(): Material | TerrainMaterial | undefined {
    return this._material;
  }

  /**
   * Setting the material re-evaluates render-mode and order based on whether
   * the base color texture is translucent. Build the material fully before
   * assigning — mutating `material.baseColor` after assignment will NOT
   * re-trigger this check. TerrainMaterial is always opaque and always
   * renders in the deferred G-buffer pass via the terrain shader variant.
   */
  public set material(material: Material | TerrainMaterial | undefined) {
    this._material = material;
    if (material instanceof Material && material.baseColor?.translucent) {
      // Transparent objects skip the G-buffer and render in the forward transparency pass
      this.renderConfiguration.order = 2000;
      this.renderConfiguration.modes = [
        RenderMode.Transparent,
        RenderMode.Picking,
        RenderMode.Depth,
      ];
    } else {
      this.renderConfiguration.order = 1000;
    }
  }

  public opacity: number = 1.0;

  /**
   * When true, renders with a 50% Bayer dither (uses the CROSSFADE shader
   * variant). Used by the asset-manager scene editor to mark scatter previews
   * so the user can distinguish them from manually placed objects.
   */
  public ghost: boolean = false;

  /**
   * Bayer-dither crossfade applied in the gbuffer pass only. Positive values
   * fade the object OUT; negative values fade it IN with the complementary
   * pattern. Used by the tiled terrain streamer to crossfade LOD swaps. Null
   * disables dithering. Not applied to the depth pass — see {@link castsShadows}.
   */
  public ditherFade: number | null = null;

  /**
   * When false, the object is skipped during the depth pass. Used by the
   * terrain streamer to mute the fading-out tile during an LOD crossfade so
   * we don't double up shadow casters or invalidate the static CSM cache
   * with a per-frame-changing dither uniform.
   */
  public castsShadows: boolean = true;

  public animationState?: AnimationState;

  public attachments?: AttachmentHolder;
  public attachmentIdentity?: Attachment;
  public boneOverrides?: Map<number, mat4>;
  public impulseLockedBones?: Set<number>;
  public ikSystem?: IkSystem;

  public get modelMatrix(): mat4 {
    return this.trs.matrix;
  }

  /**
   * Per-tile surface index/weight textures for the terrain shader. Set by
   * TiledTerrainStreamer after mesh generation; null on non-terrain objects.
   * `indices` is RG8 NEAREST (integer surface IDs), `weight` is R8 LINEAR
   * (smooth blend ramp).
   */
  public terrainSurfaceIndices: WebGLTexture | null = null;
  public terrainSurfaceWeight: WebGLTexture | null = null;

  /** Stable wrappers paired with the GL textures above. */
  public terrainSurfaceIndicesTexture: Texture | null = null;
  public terrainSurfaceWeightTexture: Texture | null = null;

  /**
   * Bounds for projecting world XZ to surface map UV:
   * [worldMinX, worldMinZ, invTileWidth, invTileDepth].
   */
  public terrainIndexMapBounds: number[] | null = null;

  public parent?: RenderObject;

  /**
   * Tag set when this RenderObject is a child of a composite (lantern body,
   * etc.). Used by the editor's picking adapter to escalate selection from a
   * clicked child to the composite parent. `undefined` for regular placements.
   */
  public compositeInstanceId?: string;

  public get animation(): Animation | undefined {
    return this.mesh.animation;
  }

  constructor(mesh: Mesh, parent?: RenderObject) {
    this.mesh = mesh;
    this.parent = parent;

    this.trs = new TRS(vec3.create());

    this.fixedRotationQuat = quat.create();
  }

  public get isAnimated(): boolean {
    return !!(this.animation && this.animation.validAnimation && this.animationState);
  }

  public rotateTo(direction: vec3): void {
    // Y-up: yaw on the XZ ground plane around the world Y axis. Compose with
    // the asset's rest orientation so any X/Z tilt baked at load-time survives.
    const yawRad = Math.atan2(direction[0], direction[2]);
    const yawQuat = quat.setAxisAngle(quat.create(), [0, 1, 0], yawRad);
    quat.multiply(yawQuat, yawQuat, this.fixedRotationQuat);
    this.trs.setRotationQuat(yawQuat);
  }

  public getPosition() {
    return this.position;
  }

  public getShaderSortKey(mode: RenderMode): number {
    let key = 0;
    if (this.isAnimated) key |= 1;
    if (this.attachmentIdentity) key |= 2;
    if (this.boneOverrides && this.boneOverrides.size > 0) key |= 16;
    if (mode === RenderMode.GBuffer || mode === RenderMode.Transparent) {
      if (this._material instanceof TerrainMaterial) key |= 32;
      else if (this._material?.baseColor && this.mesh.dataLayout.diffuseTexture) key |= 4;
      else if (this.mesh.dataLayout.color) key |= 8;
    }
    return key;
  }

  public render(ctx: Context, camera: Camera, settings: RenderSettings) {
    const gl = ctx.gl;

    let modelMatrix: mat4;
    if (this.attachmentIdentity) {
      modelMatrix = this.attachmentIdentity.modelMatrix;
    } else {
      modelMatrix = this.modelMatrix;
    }

    if (this.ikSystem && this.animationState) {
      if (!this.boneOverrides) this.boneOverrides = new Map();
      this.ikSystem.update(modelMatrix, this.boneOverrides);
    }

    if (this.attachments) {
      this.attachments.preRenderAttachments(modelMatrix);
    }

    let modelViewMatrix = mat4.multiply(mat4.create(), camera.modelViewMatrix, modelMatrix);

    switch (settings.mode) {
      case RenderMode.GBuffer:
        this.renderGBuffer(ctx, gl, camera, modelMatrix, modelViewMatrix, settings);
        break;
      case RenderMode.Transparent:
        this.renderTransparent(ctx, gl, camera, modelMatrix, modelViewMatrix, settings);
        break;
      case RenderMode.Depth:
        this.renderDepth(ctx, gl, camera, modelMatrix, modelViewMatrix, settings);
        break;
      case RenderMode.Picking:
        this.renderPicking(
          ctx,
          gl,
          camera,
          modelMatrix,
          modelViewMatrix,
          settings.pickingReference ?? 0,
        );
        break;
    }
  }

  // === G-Buffer rendering ===

  private renderGBuffer(
    ctx: Context,
    gl: WebGL2RenderingContext,
    camera: Camera,
    modelMatrix: mat4,
    modelViewMatrix: mat4,
    settings: RenderSettings,
  ) {
    if (this._material instanceof TerrainMaterial) {
      this.renderTerrainGBuffer(ctx, gl, camera, modelMatrix, modelViewMatrix, this._material);
      return;
    }

    let shader = ctx.engine.shaderManager.getGBufferShader(...this.getGBufferSettings(settings));
    ctx.setShader(shader);

    shader.uniformsContainer
      .setModelMatrix(modelMatrix)
      .setProjectionMatrix(camera.projectionMatrix)
      .setModelViewMatrix(modelViewMatrix)
      .setUnjitteredViewProjection(camera.unjitteredViewProjection)
      .setPreviousViewProjection(camera.previousViewProjection);

    if (this.ghost) {
      shader.uniformsContainer.setDitherFade(0.5).setDitherCellSize(4);
    }

    gl.bindVertexArray(this.mesh.vao);
    this.bindMaterialUniforms(shader.uniformsContainer, [1, 1, 1, 1]);

    if (settings.impostorHeightBounds) {
      shader.uniformsContainer.setImpostorHeightBounds(settings.impostorHeightBounds);
    }

    this.finalizeAndDraw(gl, shader);
  }

  private renderTerrainGBuffer(
    ctx: Context,
    gl: WebGL2RenderingContext,
    camera: Camera,
    modelMatrix: mat4,
    modelViewMatrix: mat4,
    material: TerrainMaterial,
  ) {
    const settings: GBufferTerrainShaderSettings = [];
    if (material.normalArray) settings.push('HAS_NORMAL_ARRAY');
    if (material.roughnessArray) settings.push('HAS_ROUGHNESS_ARRAY');
    if (this.ditherFade != null) settings.push('CROSSFADE');

    const shader = ctx.engine.shaderManager.getGBufferTerrainShader(...settings);
    ctx.setShader(shader);

    shader.uniformsContainer
      .setModelMatrix(modelMatrix)
      .setProjectionMatrix(camera.projectionMatrix)
      .setModelViewMatrix(modelViewMatrix)
      .setUnjitteredViewProjection(camera.unjitteredViewProjection)
      .setPreviousViewProjection(camera.previousViewProjection);

    if (this.ditherFade != null) {
      shader.uniformsContainer.setDitherFade(this.ditherFade);
    }

    gl.bindVertexArray(this.mesh.vao);
    bindTerrainMaterialUniforms(shader, material, this);

    shader.validate();
    this.draw(gl);
  }

  private getGBufferSettings(renderSettings: RenderSettings): GBufferShaderSettings {
    let settings: GBufferShaderSettings = [...this.getBaseSettings()];
    if (this.hasTexturedMaterial()) {
      this.pushMaterialMapSettings(settings);
    } else if (this.mesh.dataLayout.color) {
      settings.push('ATTRIBUTE_COLOR', 'PRESET_COLOR');
    } else {
      settings.push('PRESET_COLOR');
    }
    if (renderSettings.impostorOutput) settings.push('IMPOSTOR_OUTPUT');
    if (renderSettings.impostorHeightBounds) settings.push('IMPOSTOR_AO');
    if (this.ghost) settings.push('CROSSFADE');
    return settings;
  }

  // === Depth rendering (shadow maps) ===

  private renderDepth(
    ctx: Context,
    gl: WebGL2RenderingContext,
    camera: Camera,
    modelMatrix: mat4,
    modelViewMatrix: mat4,
    settings: RenderSettings,
  ) {
    if (!this.castsShadows) return;
    let shader = ctx.engine.shaderManager.getDepthShader(...this.getBaseSettings());
    ctx.setShader(shader);

    shader.uniformsContainer
      .setModelMatrix(modelMatrix)
      .setProjectionMatrix(camera.projectionMatrix)
      .setModelViewMatrix(modelViewMatrix);

    if (settings.depthMode == DepthMode.PointDistance) {
      shader.uniformsContainer.setDepthPoint([...settings.depthPoint!, 1.0]);
    } else {
      shader.uniformsContainer.setDepthPoint([0, 0, 0, 0]);
    }

    gl.bindVertexArray(this.mesh.vao);
    this.finalizeAndDraw(gl, shader);
  }

  // === Picking rendering ===

  private renderPicking(
    ctx: Context,
    gl: WebGL2RenderingContext,
    camera: Camera,
    modelMatrix: mat4,
    modelViewMatrix: mat4,
    pickingReference: number,
  ) {
    let shader = ctx.engine.shaderManager.getPickingShader(...this.getBaseSettings());
    ctx.setShader(shader);

    shader.uniformsContainer
      .setProjectionMatrix(camera.projectionMatrix)
      .setModelViewMatrix(modelViewMatrix)
      .setPickingColor(
        vec4.fromValues(
          (pickingReference & 0xff) / 255,
          ((pickingReference >> 8) & 0xff) / 255,
          ((pickingReference >> 16) & 0xff) / 255,
          1,
        ),
      );

    gl.bindVertexArray(this.mesh.vao);
    this.finalizeAndDraw(gl, shader);
  }

  // === Forward transparency rendering ===

  private renderTransparent(
    ctx: Context,
    gl: WebGL2RenderingContext,
    camera: Camera,
    modelMatrix: mat4,
    modelViewMatrix: mat4,
    settings: RenderSettings,
  ) {
    const csmLight = findCsmLight(settings);

    let shader = ctx.engine.shaderManager.getForwardTransparentShader(
      ...this.getTransparentSettings(!!csmLight, !!settings.volumetricTexture),
    );
    ctx.setShader(shader);

    shader.uniformsContainer
      .setModelMatrix(modelMatrix)
      .setProjectionMatrix(camera.projectionMatrix)
      .setModelViewMatrix(modelViewMatrix)
      .setAmbientColor(ctx.engine.activeScene!.lightManager.ambientColor)
      .setCameraPosition(camera.cameraPosition)
      .setOpacity(this.opacity);

    gl.bindVertexArray(this.mesh.vao);
    this.bindMaterialUniforms(shader.uniformsContainer, [1, 1, 1, 0.5]);

    bindTransparentLighting(shader.uniformsContainer, settings, camera, csmLight);

    this.finalizeAndDraw(gl, shader);
  }

  private getTransparentSettings(
    hasCSM: boolean = false,
    hasVolumetric: boolean = false,
  ): ForwardTransparentShaderSettings {
    let settings: ForwardTransparentShaderSettings = [...this.getBaseSettings()];
    if (this.hasTexturedMaterial()) {
      this.pushMaterialMapSettings(settings);
    } else {
      settings.push('PRESET_COLOR');
    }
    if (hasCSM) settings.push('SHADOW_CSM');
    if (hasVolumetric) settings.push('VOLUMETRIC');
    return settings;
  }

  /** True when the mesh has UVs and the material has a base color texture. */
  private hasTexturedMaterial(): boolean {
    const mat = this._material;
    if (!(mat instanceof Material)) return false;
    return !!(mat.baseColor && this.mesh.dataLayout.diffuseTexture);
  }

  /**
   * Push the material-map shader settings (TEXTURE + optional NORMAL_MAP /
   * METALLIC_ROUGHNESS) onto a settings array. Caller is responsible for the
   * fallback (PRESET_COLOR / ATTRIBUTE_COLOR).
   */
  private pushMaterialMapSettings(settings: string[]): void {
    const material = this._material as Material;
    settings.push('TEXTURE');
    if (material.normalMap) settings.push('NORMAL_MAP');
    if (material.metallicRoughnessMap) settings.push('METALLIC_ROUGHNESS');
  }

  /**
   * Bind base color / normal / metallic-roughness textures, or fall back to
   * a flat preset color. Used by both the GBuffer and transparent passes.
   * Callers must not pass TerrainMaterial — it has its own dedicated path.
   */
  private bindMaterialUniforms(uniforms: MaterialUniforms, fallback: number[]): void {
    const material = this._material instanceof Material ? this._material : undefined;
    if (this.hasTexturedMaterial()) {
      uniforms.setTextureSampler(material!.baseColor!);
      if (material!.normalMap) uniforms.setNormalMapSampler(material!.normalMap);
      if (material!.metallicRoughnessMap) {
        uniforms.setMetallicRoughnessSampler(material!.metallicRoughnessMap);
      }
    } else {
      uniforms.setPresetColor(material?.color ? [...material.color] : fallback);
    }
  }

  // === Shared helpers ===

  private finalizeAndDraw(gl: WebGL2RenderingContext, shader: AnyShader) {
    if (this.attachmentIdentity) {
      shader.uniformsContainer.setBoneMatrix(this.attachmentIdentity.boneMatrix);
    }
    if (this.isAnimated) {
      this.setupAnimation(shader);
    }
    this.draw(gl);
  }

  private getBaseSettings(): ('ANIMATION' | 'ATTACHMENT' | 'BONE_OVERRIDES')[] {
    const settings: ('ANIMATION' | 'ATTACHMENT' | 'BONE_OVERRIDES')[] = [];
    if (this.isAnimated) settings.push('ANIMATION');
    if (this.attachmentIdentity) settings.push('ATTACHMENT');
    if (this.boneOverrides && this.boneOverrides.size > 0) settings.push('BONE_OVERRIDES');
    return settings;
  }

  private setupAnimation(shader: AnyShader) {
    let frameSet = this.animationState!.getFrames();
    let offset = this.mesh.animationDataTexture!.getFrameOffset(
      frameSet.state,
      frameSet.frameIndex,
    );
    let dimension = this.mesh.animationDataTexture!.dimension;

    shader.uniformsContainer
      .setBindShapeMatrix(this.animation!.bindShapeMatrix)
      .setDataSettings([dimension.width, dimension.height, offset])
      .setMatrixSampler(this.mesh.animationDataTexture!);

    // Clip blending
    if (frameSet.blend) {
      const blendOffset = this.mesh.animationDataTexture!.getFrameOffset(
        frameSet.blend.state,
        frameSet.blend.frameIndex,
      );
      shader.uniformsContainer.setBlendSettings([
        dimension.width,
        dimension.height,
        blendOffset,
        frameSet.blend.weight,
      ]);
    } else {
      shader.uniformsContainer.setBlendSettings([0, 0, 0, 0]);
    }

    // Bone overrides — stored in joint-space, convert to shader format (invBind * joint, transposed)
    if (this.boneOverrides && this.boneOverrides.size > 0) {
      const anim = this.animation as GltfAnimation;
      let i = 0;
      for (const [jointIndex, jointMatrix] of this.boneOverrides) {
        if (i >= 32) break;
        // Same conversion as GltfAnimation.computeMatricesForFrame:
        // worldMatrix = jointMatrix * invBind, then transpose for v * M
        const joint = anim.joints[jointIndex];
        const clipId = frameSet.state;
        const invBind = joint.frames[clipId][0].invBindMatrix;
        const shaderMatrix = mat4.create();
        mat4.multiply(shaderMatrix, jointMatrix, invBind);
        mat4.transpose(shaderMatrix, shaderMatrix);
        shader.uniformsContainer.setBoneOverrides(i, shaderMatrix);
        shader.uniformsContainer.setBoneOverrideIndices(i, jointIndex);
        i++;
      }
      shader.uniformsContainer.setBoneOverrideCount(i);
    }
  }

  private draw(gl: WebGL2RenderingContext) {
    if (this.mesh.indexBuffer) {
      gl.drawElements(
        this.mesh.buffer.glDrawMode,
        this.mesh.indexBuffer.itemCount,
        this.mesh.indexBuffer.getIndexElementType(gl),
        0,
      );
    } else {
      gl.drawArrays(this.mesh.buffer.glDrawMode, 0, this.mesh.buffer.itemCount);
    }
  }
}

/**
 * Bind the array samplers + index map + surface props texture for the terrain shader.
 * The `obj` provides the per-tile index map and its world bounds.
 */
export function bindTerrainMaterialUniforms(
  shader: GBufferTerrainShader,
  material: TerrainMaterial,
  obj: RenderObject,
): void {
  const u = shader.uniformsContainer;
  u.setAlbedoArray(material.albedoArray);
  if (material.normalArray) u.setNormalArray(material.normalArray);
  if (material.roughnessArray) u.setRoughnessArray(material.roughnessArray);
  u.setSurfacePropsTex(material.surfacePropsTexWrapper);
  const indices = obj.terrainSurfaceIndicesTexture ?? material.trivialIndicesWrapper;
  const weight = obj.terrainSurfaceWeightTexture ?? material.trivialWeightWrapper;
  u.setSurfaceIndices(indices);
  u.setSurfaceWeight(weight);
  u.setIndexMapBoundsAndSize(obj.terrainIndexMapBounds ?? [0, 0, 1, 1]);
}
